import React, { createContext, useContext, useMemo, useRef, useState } from "react";
import styles from "./page.module.scss";
import classnames from "classnames";
import upperFirst from "lodash/upperFirst";
import { Button } from "../Button";
import { BiArrowBack } from "react-icons/bi";
import { observer } from "mobx-react-lite";
import type { PageProps } from "@whatslly/types/UI-V2/PageBuilderTypes";
import { SubHeader } from "../SubHeader";
import { useTranslation } from "react-i18next";
import globalStyles from "../globalStyles";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { AsyncImage } from "@whatslly/ui-components/AsyncImage";

export const PageContext = createContext<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { formRef: React.MutableRefObject<{ submitForm?: ((() => Promise<void>) & (() => Promise<any>)) | undefined }> } | { formRef: undefined }
>({ formRef: undefined });

export const usePageContext = () => useContext(PageContext);
// the notificationHeight should come from parent element inside the widget
export const Page: React.FC<PageProps & { disableSubmit?: boolean }> = observer(({ disableSubmit, children, ...props }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formRef = useRef<{ submitForm?: (() => Promise<void>) & (() => Promise<any>) }>({ submitForm: undefined });
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const handleRemoveSubmitting = () => {
    setIsSubmitting(false);
  };
  const allButtons = useMemo(() => {
    if (!props.footer) {
      props.footer = [];
    }
    if (!props.hideSubmitButton) {
      props.footer = [
        {
          testId: "page-submit-button",
          text: props.submitButtonText ?? "submit",
          onClick: async () => {
            if (formRef.current && formRef.current.submitForm) {
              setIsSubmitting(true);
              await formRef.current.submitForm();
              handleRemoveSubmitting();
            }
          },
          isSubmitting,
          disabled: disableSubmit || isSubmitting,
        },
        ...props.footer,
      ];
    }
    if (!props.hideCancelButton) {
      props.footer = [
        {
          text: !props.hideSubmitButton ? t("actions_cancel") : t("done"),
          onClick: props.onCancel,
          link: !props.hideSubmitButton,
          className: props?.cancelButtonClass,
        },
        ...props.footer,
      ];
    }
    return props.footer;
  }, [disableSubmit, isSubmitting, props, t]);

  return (
    <PageContext.Provider value={{ formRef }}>
      <div
        className={classnames(styles.loadingContainer, props.loaderClassName, { [styles.hideLoading]: !props.isLoading })}
        data-testid={props.isLoading ? `page-loading` : ""}
      >
        <AsyncImage alt="tuvis loader" src={LOADER_IMG} />
        <p>{t("search_person.loading")}</p>
      </div>
      <div className={classnames(styles.pageContainer, globalStyles.page, { [styles.pageIsLoading]: props.isLoading }, props.className)}>
        <div
          className={classnames(styles.page, globalStyles[`pi-${props.paddingInline}`], {
            [styles.noFooter]: !props.footer,
          })}
        >
          <div className={classnames(styles.pageContect, { [styles.noPadding]: props.noPadding }, props.pageContentClassName)}>
            {(props.backArrow || props.header) && (
              <div className={classnames(styles.pageHeaderContainer, { [styles.withOutHeader]: !props.header })}>
                {props.backArrow && <BiArrowBack className={styles.backArrow} onClick={props.onBack} />}
                {props.header && (
                  <h1
                    className={classnames(
                      styles.pageHeader,
                      globalStyles["fs-large"],
                      globalStyles["fw-default"],
                      globalStyles[`color-main-primary`]
                    )}
                  >
                    {upperFirst(props.header)}
                  </h1>
                )}
                {props.subHeader && <SubHeader text={props.subHeader} />}
              </div>
            )}
            {children}
          </div>
          {!props.hideFooter && (
            <div className={classnames(styles.pageFooter, { [styles.oneButton]: allButtons.length === 1 }, props.footerClassName)}>
              {allButtons.map((buttonData, key) => (
                <Button {...buttonData} key={key} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContext.Provider>
  );
});
